/** tm/pages/FPManagePage.tsx — FP별 관리 3컬럼 (FP목록185px|고객카드|업무상세) + 간이등록 + SMS
 *  2026-10-31 지도사 「FP 목록이 아니라 세무사 목록이 있어야겠다」 → role='tax' 이면 「세무사별 관리」(같은 화면 · fps.role 로 나눔 · 업체 잇기 · 포털은 그대로) */
import type { CSSProperties } from 'react';
import { useState, useMemo, useEffect } from 'react';
import type { TMStore } from '../hooks';
import type { TaskDecisionItem } from '../types';
import { C, T, R, Badge, PBar, DDayBadge, StatusBadge, Empty } from '../components/ui';
import { ResizeHandle, useResizable } from '../components/ResizeHandle';
import CompanyDetailPanel from '../components/CompanyDetailPanel';
import RegisterClientModal from '../components/RegisterClientModal';
import { linkSms, loanUrl } from '../linkSms';
import TaskRegisterModal from '../components/TaskRegisterModal';
import * as api from '../api';

const INSURANCE_COLORS: Record<string, string> = {
  '삼성생명': '#1428A0', '한화생명': '#FF6600', '교보생명': '#003DA5',
  'DB생명': '#00843D', '미래에셋생명': '#002B5C', '신한라이프': '#0046FF',
  '삼성화재': '#1428A0', 'DB손해보험': '#00843D', '현대해상': '#003478',
  'KB손해보험': '#FFBC00', '메리츠화재': '#E31837', '한화손해보험': '#FF6600',
};
function fpAvatar(name: string, company: string) {
  const initial = name ? name.charAt(0) : '?';
  const color = INSURANCE_COLORS[company] || '#6B7280';
  return { initial, color };
}

import type { InsuranceCompany } from '../api';

interface Props { store: TMStore; role?: 'fp' | 'tax' }

/** 팀장님께 보내는 문자 — 글은 tm/linkSms(WM님 대출 링크와 한 벌 · 2026-10-29) */
const smsText=(m:{fpName:string;fpTitle:string;url:string;kind?:'portal'|'loan'}):string=>linkSms({name:m.fpName,title:m.fpTitle||'팀장',url:m.url,kind:m.kind});

export default function FPManagePage({ store, role = 'fp' }: Props) {
  const tax = role === 'tax';
  const K = tax ? '세무사' : 'FP';            // 화면 낱말
  const KN = K + '님';                         // 사람을 부를 때는 늘 「님」(2026-10-31 지도사 「세무사님 · FP님 · WM님처럼」)
  const T0 = tax ? '세무사' : '팀장';          // 기본 호칭
  const { fps: allFps, wms, companies, tasks, consultations, govPrograms, taskTemplates, aiPresets } = store;
  const fps = useMemo(() => allFps.filter(f => ((f.role || 'fp') === 'tax') === tax), [allFps, tax]);   // 이 화면 갈래만
  const [selFPId, setSelFPId] = useState<string|null>(null);
  const [selCompId, setSelCompId] = useState<string|null>(null);

  // 업체 선택 시 최신 데이터 로드
  useEffect(() => { if (selCompId) store.refreshTasks(); }, [selCompId]);
  const [search, setSearch] = useState('');
  const [fpSearch, setFpSearch] = useState('');
  const [showAddFP, setShowAddFP] = useState(false);
  const [addForm, setAddForm] = useState({name:'',phone:'',email:'',company:'',title:tax?'세무사':'팀장'});
  const [portalModal, setPortalModal] = useState<string|null>(null);
  const [smsModal, setSmsModal] = useState<{fpName:string;phone:string;url:string;fpTitle:string;kind?:'portal'|'loan'}|null>(null);
  const [toast, setToast] = useState('');
  const [editForm, setEditForm] = useState<{name:string;phone:string;company:string;email:string;memo:string;title:string;wm_id:string}|null>(null);
  const [fpEditMode, setFpEditMode] = useState(false);
  const [showRegClient, setShowRegClient] = useState(false);
  const [showRegModal, setShowRegModal] = useState(false);
  const [insuranceCompanies, setInsuranceCompanies] = useState<InsuranceCompany[]>([]);
  useEffect(()=>{api.getInsuranceCompanies().then(r=>setInsuranceCompanies(r.data||[])).catch(()=>{});},[]);
  const col1Resize = useResizable({ initialWidth: 260, minWidth: 180, maxWidth: 500 });
  function showToast(msg:string,type?:'success'|'error'){setToast((type==='error'?'❌ ':'✓ ')+msg);setTimeout(()=>setToast(''),3000);}

  async function handleTaskRegDone(result: { decisions: TaskDecisionItem[] }) {
    try {
      for (const d of result.decisions) {
        const taskName = d.mode === 'template' ? taskTemplates.find(t => t.id === d.templateId)?.name || '업무' : d.customName || '직접입력 업무';
        const taskIcon = d.mode === 'template' ? taskTemplates.find(t => t.id === d.templateId)?.icon || '📋' : d.customIcon;
        await api.createTask({
          assigned_to: (d as { who?: string }).who || '',   // 담당(2026-10-18) — 비면 오재인
          company_id: '', template_id: d.mode === 'template' ? d.templateId : null,
          category_path: d.category_path || [], type: taskName, icon: taskIcon,
          status: d.taskStatus as 'FP요청대기' | '진행' | '완료',
          deadline: d.deadline || null, waiting_fp: d.taskStatus === 'FP요청대기',
          steps: d.steps.filter(s => s.name.trim()).map((s, i) => ({ name: s.name, type: s.type, sort_order: i, due: s.due || null, extras: (s as unknown as Record<string, unknown>).extras || {} })),   // 자료수집 설정 · 기존 자료 요청 잇기도 함께(빠지던 것 · 2026-10-28)
        } as Record<string, unknown>);
      }
      setShowRegModal(false);
      showToast(`${result.decisions.length}건 업무 등록 완료`);
      await store.refresh();
    } catch (e: unknown) { showToast(e instanceof Error ? e.message : '업무 등록 실패', 'error'); }
  }

  const fpStats = useMemo(()=>fps.map(fp=>{
    const myComps=companies.filter(c=>(c.fps||[]).some(cf=>cf.fp_id===fp.id));
    const myTasks=tasks.filter(t=>myComps.some(c=>c.id===t.company_id));
    return{...fp,comps:myComps,compCnt:myComps.length,activeTasks:myTasks.filter(t=>t.status!=='완료').length,waitingTasks:myTasks.filter(t=>t.status==='FP요청대기').length,consultCnt:consultations.filter(c=>c.fp_id===fp.id).length};
  }),[fps,companies,tasks,consultations]);

  const filteredFps = useMemo(()=>{
    if(!fpSearch.trim()) return fpStats;
    const q = fpSearch.trim().toLowerCase();
    return fpStats.filter(fp=>fp.name.toLowerCase().includes(q)||(fp.company||'').toLowerCase().includes(q));
  },[fpStats,fpSearch]);

  const selFP=fpStats.find(f=>f.id===selFPId);
  const fpComps=selFP?selFP.comps:[];
  const filteredComps=search.trim()?fpComps.filter(c=>c.name.includes(search)||(c.biz_no||'').includes(search)):fpComps;
  const selComp=selCompId?companies.find(c=>c.id===selCompId):null;

  // Initialize edit form when FP selection changes
  useMemo(()=>{
    if(selFP){setEditForm({name:selFP.name,phone:selFP.phone||'',company:selFP.company||'',email:selFP.email||'',memo:selFP.memo||'',title:selFP.title||T0,wm_id:selFP.wm_id||''});}
    else{setEditForm(null);}
    setFpEditMode(false);
  },[selFPId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleAddFP(){
    if(!addForm.name.trim()||!addForm.phone.trim())return;
    try{await api.createFp({...addForm,role} as Parameters<typeof api.createFp>[0]);await store.refresh();setAddForm({name:'',phone:'',email:'',company:'',title:T0});setShowAddFP(false);showToast(`${KN}을 등록했어요`);}
    catch(e:unknown){showToast(e instanceof Error?e.message:'등록 실패','error');}
  }

  async function handleEditFP(){
    if(!selFP||!editForm)return;
    if(!editForm.phone.trim()){showToast('휴대폰번호는 필수입니다','error');return;}
    try{await api.updateFp(selFP.id,{name:editForm.name,phone:editForm.phone,company:editForm.company,email:editForm.email,memo:editForm.memo,title:editForm.title||T0,wm_id:editForm.wm_id||null});await store.refresh();showToast(`${KN} 정보를 고쳤어요`);}
    catch(e:unknown){showToast(e instanceof Error?e.message:'수정 실패','error');}
  }

  async function handleGenerateToken(){
    if(!selFP)return;
    try{
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`/api/tm/fps/${selFP.id}/generate-token`, {
        method: 'POST', headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' }
      });
      if(!res.ok){const json=await res.json().catch(()=>({}));throw new Error((json as Record<string,string>).error||`API ${res.status}`);}
      await store.refresh();showToast('포털 토큰이 생성되었습니다');
    }catch(e:unknown){showToast(e instanceof Error?e.message:'토큰 생성 실패','error');}
  }

  async function handleSendSms(){
    if(!smsModal)return;
    // 고친 문구 그대로 보낸다(예전엔 칸을 고쳐도 기본 문구가 나갔음 · 2026-10-13)
    const box=document.getElementById('_smsText');
    const text=box instanceof HTMLTextAreaElement&&box.value.trim()?box.value:smsText(smsModal);
    try{await api.sendSms({to:smsModal.phone,message:text});setSmsModal(null);showToast('SMS 발송 완료');}
    catch(e:unknown){showToast(e instanceof Error?e.message:'발송 실패','error');}
  }

  async function handleUpdate(id:string,patch:Parameters<typeof api.updateCompany>[1]){
    await store.saveCompany(id,patch);   // 그 업체 한 곳만 갱신(2026-10-26)
  }

  const portalBase='https://roum-partners.com/fp/';
  // 열쇠(&k=)는 싣지 않는다 — 팀장님이 이 문자를 통째로 대표님께 넘기면 그 대표님이 팀장님의 다른 대출까지 보게 됐다(2026-10-28 검토) · 인증번호 없이 보기는 팀장님 포털을 연 기기에서만
  const fpLoanUrl=(f:{loan_token?:string|null})=>loanUrl(f.loan_token);   // 팀장님께 보내는 링크 = 열면 팀장님이 적기가 기본(2026-10-20 지도사)
  const getInsColor=(companyName:string)=>{const ic=insuranceCompanies.find(ic=>companyName&&companyName.includes(ic.name));return ic?.color||null;};

  return(
    <div style={{display:'flex',height:'100%',overflow:'hidden'}}>
      {/* 컬럼1: FP 네비 185px */}
      <div style={{width:185,borderRight:`1px solid ${C.border}`,background:C.bgAlt,display:'flex',flexDirection:'column',overflow:'hidden',flexShrink:0}}>
        <div style={{padding:'8px 10px',borderBottom:`1px solid ${C.border}`,flexShrink:0,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <span style={{fontSize:T.xs,fontWeight:700,color:C.muted,textTransform:'uppercase',letterSpacing:'.4px'}}>{KN} 목록</span>
          <button onClick={()=>setShowAddFP(!showAddFP)} style={{fontSize:T.xs,padding:'2px 6px',borderRadius:R.sm,border:'none',background:C.accent,color:'#fff',cursor:'pointer',fontWeight:700}}>+ {KN}</button>
        </div>
        <div style={{padding:'6px 8px',borderBottom:`1px solid ${C.border}`,flexShrink:0}}>
          <input value={fpSearch} onChange={e=>setFpSearch(e.target.value)} placeholder={`${KN} 검색 (이름/소속)`} style={{width:'100%',padding:'5px 8px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:T.xs,outline:'none',boxSizing:'border-box' as const}}/>
        </div>
        {showAddFP&&(
          <div style={{padding:8,borderBottom:`1px solid ${C.border}`,background:'#F0FAF5',flexShrink:0}}>
            <div style={{fontSize:T.xs,fontWeight:700,color:C.accent,marginBottom:4}}>{KN} 새로 등록</div>
            {([['성명 *','name'],['호칭','title'],['연락처 *','phone'],['이메일','email']] as [string,keyof typeof addForm][]).map(([lb,k])=>(
              <input key={k} value={addForm[k]} onChange={e=>setAddForm(p=>({...p,[k]:e.target.value}))} placeholder={lb} style={{width:'100%',padding:'5px 7px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:T.xs,outline:'none',boxSizing:'border-box' as const,marginBottom:3}}/>
            ))}
            <select value={insuranceCompanies.some(ic=>ic.name===addForm.company)?addForm.company:'__custom'} onChange={e=>{
              if(e.target.value==='__custom')setAddForm(p=>({...p,company:''}));
              else setAddForm(p=>({...p,company:e.target.value}));
            }} style={{width:'100%',padding:'5px 7px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:T.xs,outline:'none',background:C.white,marginBottom:3,boxSizing:'border-box' as const}}>
              <option value="">소속 보험사 (선택)</option>
              {insuranceCompanies.map(ic=><option key={ic.id} value={ic.name}>{ic.name}</option>)}
              <option value="__custom">직접입력</option>
            </select>
            {(!addForm.company||!insuranceCompanies.some(ic=>ic.name===addForm.company))&&addForm.company!==''&&(
              <input value={addForm.company} onChange={e=>setAddForm(p=>({...p,company:e.target.value}))} placeholder="소속 직접입력" style={{width:'100%',padding:'5px 7px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:T.xs,outline:'none',boxSizing:'border-box' as const,marginBottom:3}}/>
            )}
            <button onClick={handleAddFP} style={{width:'100%',padding:'6px 0',borderRadius:R.sm,border:'none',background:C.accent,color:'#fff',fontSize:T.xs,fontWeight:700,cursor:'pointer'}}>등록</button>
          </div>
        )}
        <div style={{flex:1,overflowY:'auto'}}>
          {filteredFps.map(fp=>{const act=selFPId===fp.id;const insColor=getInsColor(fp.company||'');return(
            <div key={fp.id} onClick={()=>{setSelFPId(act?null:fp.id);setSelCompId(null);setSearch('');}}
              style={{padding:'8px 10px',cursor:'pointer',background:act?C.accentLt:'transparent',borderBottom:`1px solid ${C.border}`}}>
              <div style={{display:'flex',alignItems:'center',gap:6}}>
                <div style={{width:26,height:26,borderRadius:R.md,background:insColor?`linear-gradient(135deg,${insColor},${insColor}cc)`:`linear-gradient(135deg,${C.accent},#059669)`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:T.xs,fontWeight:700,color:'#fff',flexShrink:0}}>{fp.name?fp.name.charAt(0):'?'}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:T.xs,fontWeight:act?700:500,color:act?C.accent:C.text,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{fp.name}</div>
                  <div style={{fontSize:T.xs,color:insColor||C.muted,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{fp.company}</div>
                </div>
              </div>
              <div style={{display:'flex',gap:4,marginTop:4}}>
                <span style={{fontSize:T.xs,color:C.muted}}>{fp.compCnt}업체</span>
                <span style={{fontSize:T.xs,color:C.accent}}>{fp.activeTasks}건</span>
                {fp.waitingTasks>0&&<span style={{fontSize:T.xs,color:C.purple,fontWeight:700}}>대기{fp.waitingTasks}</span>}
              </div>
            </div>
          );})}
          {filteredFps.length===0&&fpSearch.trim()&&<div style={{padding:16,textAlign:'center',color:C.muted,fontSize:T.xs}}>검색 결과 없음</div>}
          {fps.length===0&&<div style={{padding:16,textAlign:'center',color:C.muted,fontSize:T.xs}}>등록된 {KN}이 없어요</div>}
        </div>
      </div>

      {/* 컬럼2: FP 정보 + 업체 카드 240px */}
      <div style={{width:col1Resize.width,borderRight:`1px solid ${C.border}`,display:'flex',flexDirection:'column',overflow:'hidden',background:C.bgAlt,flexShrink:0}}>
        <div style={{borderBottom:`1px solid ${C.border}`,background:C.white,flexShrink:0}}>
          {selFP&&editForm?<>
            {/* FP 정보 카드 */}
            <div style={{padding:'8px 10px'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                <div style={{fontSize:T.md,fontWeight:800}}>{selFP.name} {KN}</div>
                {!fpEditMode&&<button onClick={()=>setFpEditMode(true)} style={{fontSize:T.xs,padding:'2px 8px',borderRadius:R.sm,border:`1px solid ${C.accent}`,background:C.accentLt,color:C.accent,cursor:'pointer',fontWeight:600}}>✏️ 수정</button>}
              </div>
              {fpEditMode ? <>
                {([['이름','name'],['호칭','title'],['휴대폰번호(필수)','phone'],['이메일','email'],['비고','memo']] as [string,'name'|'title'|'phone'|'email'|'memo'][]).map(([lb,k])=>(
                  <div key={k} style={{marginBottom:4}}>
                    <div style={{fontSize:T.xs,color:C.muted,fontWeight:600,marginBottom:1}}>{lb}</div>
                    <input value={editForm[k]} onChange={e=>setEditForm(p=>p?{...p,[k]:e.target.value}:p)} style={{width:'100%',padding:'4px 7px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:T.xs,outline:'none',boxSizing:'border-box' as const}}/>
                  </div>
                ))}
                <div style={{marginBottom:4}}>
                  <div style={{fontSize:T.xs,color:C.muted,fontWeight:600,marginBottom:1}}>소속</div>
                  <select value={insuranceCompanies.some(ic=>ic.name===editForm.company)?editForm.company:'__custom'} onChange={e=>{
                    if(e.target.value==='__custom')setEditForm(p=>p?{...p,company:''}:p);
                    else setEditForm(p=>p?{...p,company:e.target.value}:p);
                  }} style={{width:'100%',padding:'4px 7px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:T.xs,outline:'none',background:C.white,boxSizing:'border-box' as const}}>
                    <option value="">선택</option>
                    {insuranceCompanies.map(ic=><option key={ic.id} value={ic.name}>{ic.name}</option>)}
                    <option value="__custom">직접입력</option>
                  </select>
                  {editForm.company&&!insuranceCompanies.some(ic=>ic.name===editForm.company)&&(
                    <input value={editForm.company} onChange={e=>setEditForm(p=>p?{...p,company:e.target.value}:p)} placeholder="소속 직접입력" style={{width:'100%',padding:'4px 7px',borderRadius:R.sm,border:`1px solid ${C.border}`,fontSize:T.xs,outline:'none',boxSizing:'border-box' as const,marginTop:3}}/>
                  )}
                </div>
                {/* WM — 대시보드 「WM 없는 FP」에서 오면 여기서 바로 고른다(2026-10-18 빠진 입력 조사: 고를 칸이 없었음) · 이 FP 의 새 고객 업체 WM 기본값 */}
                <div style={{marginBottom:4}}>
                  <div style={{fontSize:T.xs,color:C.muted,fontWeight:600,marginBottom:1}}>WM</div>
                  <select value={editForm.wm_id} onChange={e=>setEditForm(p=>p?{...p,wm_id:e.target.value}:p)} style={{width:'100%',padding:'4px 7px',borderRadius:R.sm,border:`1px solid ${editForm.wm_id?C.border:'#F59E0B'}`,fontSize:T.xs,outline:'none',background:C.white,boxSizing:'border-box' as const}}>
                    <option value="">WM 없음</option>
                    {wms.map(w=><option key={w.id} value={w.id}>{w.name}{w.company?` (${w.company})`:''}</option>)}
                  </select>
                </div>
                <div style={{display:'flex',gap:4,marginTop:4}}>
                  <button onClick={()=>{setFpEditMode(false);setEditForm({name:selFP.name,phone:selFP.phone||'',company:selFP.company||'',email:selFP.email||'',memo:selFP.memo||'',title:selFP.title||T0,wm_id:selFP.wm_id||''});}} style={{flex:1,padding:'5px 0',borderRadius:R.sm,border:`1px solid ${C.border}`,background:C.white,fontSize:T.xs,cursor:'pointer',color:C.muted}}>취소</button>
                  <button onClick={()=>{handleEditFP().then(()=>setFpEditMode(false));}} style={{flex:2,padding:'5px 0',borderRadius:R.sm,border:'none',background:C.accent,color:'#fff',fontSize:T.xs,fontWeight:700,cursor:'pointer'}}>저장</button>
                </div>
                <button onClick={async()=>{
                  if(!window.confirm(selFP.name+' '+KN+'을 삭제할까요?\n목록에서 숨겨지고 업체 연결이 해제됩니다. 같은 번호로 다시 등록하면 복구됩니다.'))return;
                  try{await api.deleteFp(selFP.id);await store.refresh();setSelFPId(null);showToast(`${KN}을 삭제했어요`);}
                  catch(e:unknown){showToast(e instanceof Error?e.message:'삭제 실패','error');}
                }} style={{color:C.red,border:`1px solid ${C.red}`,background:C.white,padding:'6px 14px',borderRadius:R.sm,cursor:'pointer',fontSize:T.xs,width:'100%',marginTop:6}}>🗑 {KN} 삭제</button>
              </> : <>
                {([['이름',selFP.name],['휴대폰',selFP.phone||'—'],['소속',selFP.company||'—'],['이메일',selFP.email||'—'],['비고',selFP.memo||'—']] as [string,string][]).map(([lb,val])=>(
                  <div key={lb} style={{display:'flex',gap:6,padding:'3px 0',borderBottom:`1px solid ${C.border}`}}>
                    <span style={{fontSize:T.xs,color:C.muted,fontWeight:600,minWidth:48,flexShrink:0}}>{lb}</span>
                    <span style={{fontSize:T.xs,color:C.text,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{val}</span>
                  </div>
                ))}
              </>}
            </div>
            {/* FP 헤더 버튼 — 이름 칸 + 열기 · 복사 · 문자 발송 세 칸(오와 열 · 2026-10-13 지도사 「이 위치도 고쳐 봐」: 두 줄이 어긋나고 복사 버튼이 꺼진 것처럼 흐렸음) */}
            {(()=>{
              const cell:CSSProperties={height:28,padding:'0 6px',borderRadius:R.sm,fontSize:T.xs,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',minWidth:0};
              const open:CSSProperties={...cell,border:`1px solid ${C.accent}`,background:C.accentLt,color:C.accent};
              const copy:CSSProperties={...cell,border:`1px solid ${C.border}`,background:C.white,color:C.text};
              const send:CSSProperties={...cell,border:`1px solid ${C.blue}`,background:C.blueLt,color:C.blue};
              const label:CSSProperties={fontSize:T.xs,color:C.sub,fontWeight:600,whiteSpace:'nowrap'};
              const grid:CSSProperties={display:'grid',gridTemplateColumns:'58px repeat(3,minmax(0,1fr))',gap:4,alignItems:'center'};
              return (
                <div style={{padding:'8px 10px',borderTop:`1px solid ${C.border}`,display:'flex',flexDirection:'column',gap:4}}>
                  <div style={grid}>
                    <span style={label}>업체</span>
                    <button onClick={()=>setShowRegClient(true)} style={{...open,gridColumn:'2 / span 3'}}>+ 업체 등록</button>
                  </div>
                  {selFP.portal_token?(
                    <>
                      <div style={grid}>
                        <span style={label}>{KN} 포털</span>
                        <button onClick={()=>setPortalModal(selFP.portal_token||selFP.id)} style={open}>🌐 열기</button>
                        <button onClick={()=>{navigator.clipboard.writeText(portalBase+selFP.portal_token).catch(()=>{});showToast('포털 링크 복사됨');}} style={copy}>📋 복사</button>
                        <button onClick={()=>setSmsModal({fpName:selFP.name,phone:selFP.phone||'',url:portalBase+selFP.portal_token,fpTitle:selFP.title||T0})} style={send}>📨 문자 발송</button>
                      </div>
                      {selFP.loan_token&&(
                        <div style={grid} title="팀장님 정책자금 대출 접수 링크 — 열면 팀장님이 적기 · 대표님께는 그 화면의 「대표님께 보내기」">
                          <span style={label}>대출 링크</span>
                          <button onClick={()=>window.open(fpLoanUrl(selFP),'_blank','noopener')} style={open}>💰 열기</button>
                          <button onClick={()=>{navigator.clipboard.writeText(fpLoanUrl(selFP)).catch(()=>{});showToast('대출 요청 링크 복사됨');}} style={copy}>📋 복사</button>
                          <button onClick={()=>setSmsModal({fpName:selFP.name,phone:selFP.phone||'',url:fpLoanUrl(selFP),fpTitle:selFP.title||T0,kind:'loan'})} style={send}>📨 문자 발송</button>
                        </div>
                      )}
                    </>
                  ):(
                    <div style={grid}>
                      <span style={label}>{KN} 포털</span>
                      <button onClick={handleGenerateToken} style={{...open,gridColumn:'2 / span 3'}}>포털 토큰 생성</button>
                    </div>
                  )}
                </div>
              );
            })()}
            {/* 업체 수 + 검색 */}
            <div style={{padding:'6px 10px',borderTop:`1px solid ${C.border}`}}>
              <div style={{fontSize:T.xs,color:C.muted}}>{filteredComps.length}개 업체</div>
              {fpComps.length>3&&<input value={search} onChange={e=>{setSearch(e.target.value);setSelCompId(null);}} placeholder="🔍 업체 검색" style={{width:'100%',padding:'5px 8px',borderRadius:6,border:`1px solid ${C.border}`,fontSize:T.xs,outline:'none',boxSizing:'border-box' as const,marginTop:4}}/>}
            </div>
          </>:<div style={{padding:'8px 10px',fontSize:T.sm,color:C.muted}}>← {KN}을 골라 주세요</div>}
        </div>
        <div style={{flex:1,overflowY:'auto',padding:'8px 8px'}}>
          {!selFPId&&<div style={{padding:20,textAlign:'center',color:C.muted,fontSize:T.sm}}>왼쪽에서<br/>{KN}을 골라 주세요</div>}
          {selFPId&&filteredComps.length===0&&<Empty icon="🏢" message="담당 업체 없음"/>}
          {filteredComps.map(c=>{
            const compTasks=tasks.filter(t=>t.company_id===c.id).sort((a,b)=>(a.status==='완료'?1:0)-(b.status==='완료'?1:0)||(dDayN(a.deadline)??999)-(dDayN(b.deadline)??999));
            const act=selCompId===c.id;
            return(
              <div key={c.id} onClick={()=>setSelCompId(act?null:c.id)}
                style={{padding:'10px 12px',borderRadius:10,border:`2px solid ${act?C.accent:C.border}`,background:act?C.accentLt:C.white,cursor:'pointer',marginBottom:6}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:compTasks.length>0?4:0}}>
                  <div style={{fontSize:T.sm,fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1,marginRight:4}}>{c.name}</div>
                  {compTasks.length>0?<Badge bg={C.accentLt} color={C.accent}>{compTasks.length}건</Badge>:<StatusBadge status={c.tm_status}/>}
                </div>
                {compTasks.length>0?compTasks.map(t=><div key={t.id} style={{display:'flex',alignItems:'center',gap:5,fontSize:11,color:t.status==='완료'?C.muted:'#4B5563',padding:'2px 0'}}><span style={{fontSize:12,flexShrink:0}}>{t.icon||'📋'}</span><span style={{flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',textDecoration:t.status==='완료'?'line-through':'none'}}>{t.type}</span>{t.deadline&&<DDayBadge deadline={t.deadline}/>}</div>):<div style={{fontSize:T.xs,color:C.muted}}>업무 없음</div>}
              </div>
            );
          })}
        </div>
      </div>

      <ResizeHandle onMouseDown={col1Resize.onMouseDown} />

      {/* 컬럼3: 업체 상세 */}
      <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
        {!selComp?<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%',color:C.muted,fontSize:T.md,flexDirection:'column',gap:8}}><span style={{fontSize:32}}>👆</span>업체를 선택하면 상세가 표시됩니다</div>:(
          <>
            <CompanyDetailPanel company={selComp} tasks={tasks.filter(t=>t.company_id===selComp.id)} consultations={consultations.filter(c=>c.company_id===selComp.id)} fps={fps} govPrograms={govPrograms} taskTemplates={taskTemplates} taskCategories={store.taskCategories} aiPresets={aiPresets} onClose={()=>setSelCompId(null)} onUpdate={handleUpdate} onToast={showToast} onRefresh={store.refresh}/>
          </>
        )}
      </div>

      {/* SMS 모달 */}
      {smsModal&&(
        <div style={{position:'fixed',inset:0,zIndex:400}}>
          <div onClick={()=>setSmsModal(null)} style={{position:'absolute',inset:0,background:'rgba(0,0,0,0.4)'}}/>
          <div style={{position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)',width:420,maxWidth:'94vw',background:C.white,borderRadius:16,overflow:'hidden',boxShadow:'0 24px 60px rgba(0,0,0,0.2)'}}>
            <div style={{padding:'14px 18px',background:`linear-gradient(135deg,${C.blue},#1d4ed8)`,color:'#fff'}}>
              <div style={{fontSize:T.lg,fontWeight:800}}>📤 {smsModal.kind==='loan'?'대출 요청 링크 발송':`${KN} 포털 링크 발송`}</div>
              <div style={{fontSize:T.xs,opacity:.85}}>{smsModal.fpName} {K}님께 SMS 발송</div>
            </div>
            <div style={{padding:18}}>
              <div style={{fontSize:T.xs,fontWeight:700,color:C.muted,marginBottom:6}}>수신 번호: {smsModal.phone||'(번호 없음)'}</div>
              <textarea id="_smsText" rows={smsModal.kind==='loan'?8:4} defaultValue={smsText(smsModal)} style={{width:'100%',padding:'10px 12px',borderRadius:R.md,border:`1px solid ${C.border}`,fontSize:T.sm,lineHeight:1.7,outline:'none',resize:'vertical' as const,boxSizing:'border-box' as const}}/>
              <div style={{display:'flex',gap:6,marginTop:12}}>
                <button onClick={()=>setSmsModal(null)} style={{flex:1,padding:11,borderRadius:R.md,border:`1px solid ${C.border}`,background:C.white,fontSize:T.sm,cursor:'pointer'}}>취소</button>
                <button onClick={handleSendSms} style={{flex:2,padding:11,borderRadius:R.md,border:'none',background:C.blue,color:'#fff',fontSize:T.sm,fontWeight:700,cursor:'pointer'}}>📤 SMS 발송</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* 업체 등록 모달 */}
      {showRegClient&&selFP&&<RegisterClientModal fps={fps} wms={wms} onSave={()=>{setShowRegClient(false);store.refresh();}} onClose={()=>setShowRegClient(false)} onToast={showToast} onRefresh={store.refresh} defaultFpId={selFP.id} companies={companies}/>}
      {showRegModal && (
        <TaskRegisterModal
          title="업무 등록"
          companyId={selComp?.id}
          fpId={selFP?.id}
          templates={taskTemplates}
          taskCategories={store.taskCategories}
          aiPresets={aiPresets}
          onDone={handleTaskRegDone}
          onCancel={() => setShowRegModal(false)}
          onToast={(msg) => showToast(msg)}
        />
      )}

      {/* FP 포털 미리보기 모달 */}
      {portalModal && (
        <div style={{position:'fixed',inset:0,zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div onClick={()=>setPortalModal(null)} style={{position:'absolute',inset:0,background:'rgba(17,24,39,0.5)'}}/>
          <div style={{position:'relative',width:390,height:'88vh',maxHeight:780,borderRadius:28,overflow:'hidden',boxShadow:'0 20px 60px rgba(0,0,0,0.2)',border:'4px solid #e5e7eb',background:'#fff',display:'flex',flexDirection:'column'}}>
            <div style={{height:20,background:'#f0f0f0',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
              <div style={{width:60,height:4,borderRadius:3,background:'#ccc'}}></div>
            </div>
            <div style={{width:'100%',height:'100%',overflow:'hidden'}}>{/* 팀장님이 실제로 보는 포털(/fp/…) 그대로 — 옛 미리보기(FPPortalStandalone)는 갈색 머리 띠의 옛 모습이었다(2026-10-18 지도사 「한화생명 색상도 아니야」) */}<iframe title="팀장님 포털 미리보기" src={`/fp/${encodeURIComponent(portalModal)}`} style={{border:'none',width:'100%',height:'100%',display:'block'}} /></div>
            <div style={{height:12,background:'#f0f0f0',flexShrink:0}}></div>
            <button onClick={()=>setPortalModal(null)} style={{position:'absolute',top:8,right:8,width:28,height:28,borderRadius:'50%',border:'none',background:'rgba(0,0,0,0.5)',color:'#fff',fontSize:T.md,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
          </div>
        </div>
      )}
      {toast&&<div style={{position:'fixed',bottom:24,left:'50%',transform:'translateX(-50%)',padding:'12px 24px',borderRadius:R.md,background:C.text,color:'#fff',fontSize:T.sm,fontWeight:600,zIndex:9999}}>{toast}</div>}
    </div>
  );
}
function dDayN(d:string|null){if(!d)return null;const _t=new Date(d);_t.setHours(0,0,0,0);const _n=new Date();_n.setHours(0,0,0,0);return Math.round((_t.getTime()-_n.getTime())/86400000);}
